function composerFileLinkBasename(path) {
    const separatorIndex = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
    return separatorIndex >= 0 ? path.slice(separatorIndex + 1) : path;
}
function escapeMarkdownLinkLabel(label) {
    return label.replaceAll("\\", "\\\\").replaceAll("[", "\\[").replaceAll("]", "\\]");
}
function encodeMarkdownLinkDestination(path) {
    return encodeURI(path)
        .replaceAll("(", "%28")
        .replaceAll(")", "%29")
        .replaceAll("#", "%23")
        .replaceAll("?", "%3F")
        .replaceAll("\\", "%5C");
}
export function serializeComposerFileLink(path) {
    const label = escapeMarkdownLinkLabel(composerFileLinkBasename(path));
    return `[${label}](${encodeMarkdownLinkDestination(path)})`;
}
function clampCursor(text, cursor) {
    if (!Number.isFinite(cursor))
        return text.length;
    return Math.max(0, Math.min(text.length, Math.floor(cursor)));
}
function isWhitespace(char) {
    return char === " " || char === "\n" || char === "\t" || char === "\r";
}
/**
 * Detect an active trigger (@path, $skill, /command) at the cursor position.
 *
 * Accepts an optional `isWhitespaceChar` override so callers with inline
 * placeholder characters can treat those as token boundaries.
 */
export function detectComposerTrigger(text, cursorInput, isWhitespaceChar) {
    const cursor = clampCursor(text, cursorInput);
    const lineStart = text.lastIndexOf("\n", Math.max(0, cursor - 1)) + 1;
    const linePrefix = text.slice(lineStart, cursor);
    if (linePrefix.startsWith("/")) {
        const commandMatch = /^\/(\S*)$/.exec(linePrefix);
        if (commandMatch) {
            const commandQuery = commandMatch[1] ?? "";
            if (commandQuery.toLowerCase() === "model") {
                return {
                    kind: "slash-model",
                    query: "",
                    rangeStart: lineStart,
                    rangeEnd: cursor,
                };
            }
            return {
                kind: "slash-command",
                query: commandQuery,
                rangeStart: lineStart,
                rangeEnd: cursor,
            };
        }
        const modelMatch = /^\/model(?:\s+(.*))?$/.exec(linePrefix);
        if (modelMatch) {
            return {
                kind: "slash-model",
                query: (modelMatch[1] ?? "").trim(),
                rangeStart: lineStart,
                rangeEnd: cursor,
            };
        }
    }
    const wsCheck = isWhitespaceChar ?? isWhitespace;
    let tokenIdx = cursor - 1;
    while (tokenIdx >= 0 && !wsCheck(text[tokenIdx] ?? "")) {
        tokenIdx -= 1;
    }
    const tokenStart = tokenIdx + 1;
    const token = text.slice(tokenStart, cursor);
    if (token.startsWith("$")) {
        return {
            kind: "skill",
            query: token.slice(1),
            rangeStart: tokenStart,
            rangeEnd: cursor,
        };
    }
    if (!token.startsWith("@")) {
        return null;
    }
    return {
        kind: "path",
        query: token.slice(1),
        rangeStart: tokenStart,
        rangeEnd: cursor,
    };
}
export function replaceTextRange(text, rangeStart, rangeEnd, replacement) {
    const safeStart = Math.max(0, Math.min(text.length, rangeStart));
    const safeEnd = Math.max(safeStart, Math.min(text.length, rangeEnd));
    const nextText = `${text.slice(0, safeStart)}${replacement}${text.slice(safeEnd)}`;
    return { text: nextText, cursor: safeStart + replacement.length };
}
